import React, { useMemo } from 'react'
import ExpandableTableRow from './MyTableExpandableRow'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Link } from 'react-router-dom'
import ConfirmationButton from '../../misc/ConfirmationButton'
import dayjs from 'dayjs'
import { Cancel, Save } from '@mui/icons-material'
import { TableRow, TableCell, Tooltip, IconButton, lighten, darken } from '@mui/material'
import MyTableCell from './MyTableCell'

const MyTableEditableRow = ({
  row,
  mode,
  tableOptions,
  editMode,
  rowid,
  editingRow,
  onValueChange,
  onDateChange,
  setEditingRow,
  handleChangeFile,
  childRef,
  isRowEditing,
  setEditMode,
  setRowId,
  setPreviousRow,
  save,
  deleteRow,
  cancel,
  rowBgColor
}) => {
  const cellStyle = { height: '30px', padding: 10, whiteSpace: 'nowrap' }

  const EDIT_ROW_COLOR = '#FFFFFF'
  const rowStyle = useMemo(
    () => ({
      backgroundColor: isRowEditing(row) ? EDIT_ROW_COLOR : rowBgColor,
      '&:hover': {
        backgroundColor: isRowEditing(row) ? EDIT_ROW_COLOR : darken(rowBgColor, 0.05)
      }
    }),
    [rowBgColor, row, isRowEditing]
  )

  const getField = (name, data) => {
    const fields = name.split('.')
    let dataField = data
    for (let i = 0; i < fields.length; i++) {
      if (dataField == null) break
      dataField = dataField[fields[i]]
    }

    const columnMeta = tableOptions.columns.find((c) => c.name === name || c.display === name)
    if (!columnMeta) return dataField

    // Type conversion
    if (columnMeta.type === 'currency') return dataField + '€'
    if (columnMeta.type === 'date' && dataField != null)
      return dayjs(dataField).format(columnMeta.format || 'DD/MM/YYYY')

    // Mapping
    if (columnMeta.mapping) return columnMeta.mapping(dataField, data)

    return dataField
  }

  const buildActionCell = (row) => {
    if (!tableOptions.actions) return null
    return (
      <TableCell style={cellStyle}>
        {tableOptions.actions.view && (
          <Tooltip title='Visualiser'>
            <IconButton
              aria-label='view'
              component={Link}
              state={{ id: row.id, history: `/${tableOptions.objectType}/view` }}
              to={`/${tableOptions.objectType}/view`}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}

        {!isRowEditing(row) ? (
          <Tooltip title='Modifier'>
            <IconButton
              aria-label='edit'
              onClick={() => {
                setEditMode(true)
                setRowId(row.id ?? row.id_tmp)
                setEditingRow({ ...row })
                setPreviousRow({ ...row })
              }}
              disabled={
                tableOptions.actions.editDisabled?.(row) || (editMode && rowid !== row.id && rowid !== row.id_tmp)
              }
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title='Enregistrer'>
            <IconButton aria-label='save' onClick={save} disabled={tableOptions.actions.editDisabled?.(row)}>
              <Save />
            </IconButton>
          </Tooltip>
        )}

        {isRowEditing(row) && (
          <Tooltip title='Annuler'>
            <IconButton aria-label='cancel' onClick={cancel} disabled={tableOptions.actions.editDisabled?.(row)}>
              <Cancel />
            </IconButton>
          </Tooltip>
        )}

        {!isRowEditing(row) && tableOptions.actions.delete && (
          <ConfirmationButton
            type='Icon'
            tooltip='Supprimer'
            message={
              tableOptions.actions.delete.field
                ? `${tableOptions.actions.delete.message} ${row[tableOptions.actions.delete.field]}?`
                : tableOptions.actions.delete.message
            }
            color='primary'
            aria-label='Supprimer'
            disabled={
              tableOptions.actions.editDisabled?.(row) || (editMode && rowid !== row.id && rowid !== row.id_tmp)
            }
            onClickOk={() => deleteRow(row.id ?? row.id_tmp)}
          >
            <DeleteIcon />
          </ConfirmationButton>
        )}

        {tableOptions.actions.customActions?.map((action, idx) => (
          <Tooltip key={`tooltip_${idx}`} title={action.title}>
            <IconButton onClick={() => action.onClick(row.id)}>{action.icon()}</IconButton>
          </Tooltip>
        ))}
      </TableCell>
    )
  }

  const renderCells = () =>
    tableOptions.columns.map((column) => {
      if (column.display === false) return null
      const isEditing = isRowEditing(row)
      const dataSource = isEditing ? editingRow : row
      return (
        <TableCell style={cellStyle} key={column.name}>
          <MyTableCell
            value={column.display ? getField(column.display, dataSource) : getField(column.name, dataSource)}
            component={column.component}
            editMode={isEditing}
            field={column}
            data={dataSource}
            onValueChange={onValueChange}
            onDateChange={onDateChange}
            ref={(ref) => ref && childRef.current.push(ref)}
            editRow={editingRow}
            onRowDataChange={setEditingRow}
            defaultValueFunction={column.defaultValueFunction}
            handleChangeFile={handleChangeFile}
          />
        </TableCell>
      )
    })

  const rowKey = row.id || row.id_tmp

  return tableOptions.isExpand ? (
    <ExpandableTableRow
      key={rowKey}
      sx={rowStyle}
      expandComponent={<MyTableCell component={tableOptions.expand} value={row} />}
    >
      {renderCells()}
      {mode === 'edit' && buildActionCell(row)}
    </ExpandableTableRow>
  ) : (
    <TableRow key={rowKey} sx={rowStyle}>
      {renderCells()}
      {mode === 'edit' && buildActionCell(row)}
    </TableRow>
  )
}

export default React.memo(MyTableEditableRow, (prev, next) => {
  if (!next.isRowEditing(next.row) && !prev.isRowEditing(prev.row)) {
    return prev.row === next.row
  }
  return false
})
